import React, { useState } from 'react';
import { Dialog, MobileDialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import { InlineLoader } from '@/components/ui/loading';
import { supabase } from '@/integrations/supabase/client';
import { useTranslatedToast } from '@/hooks/useTranslatedToast';
import { useTranslation } from '@/hooks/useTranslation';
import { CompanySubscriptionTier, CompanyBillingData } from '@/types/billing';
import { formatPrice } from '@/utils/billing/billingFormatters';

interface CheckoutPaymentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTier: CompanySubscriptionTier;
  currentTier: CompanySubscriptionTier | null;
  companyData: CompanyBillingData;
  onSuccess: () => void;
}

interface CheckoutSessionResponse {
  success: boolean;
  sessionId?: string;
  sessionUrl?: string;
  error?: string;
}

const CheckoutPaymentForm: React.FC<{
  selectedTier: CompanySubscriptionTier;
  currentTier: CompanySubscriptionTier | null;
  companyData: CompanyBillingData;
  onPaymentSuccess: () => void;
}> = ({
  selectedTier,
  currentTier,
  companyData,
  onPaymentSuccess
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const toast = useTranslatedToast();
  const { t } = useTranslation('pages');

  const createCheckoutSession = async () => {
    const { data, error } = await supabase.functions.invoke(
      'create-checkout-session',
      { 
        body: {
          companyId: companyData.company.id,
          paymentType: 'subscription',
          tierKey: selectedTier.tier_key,
          successUrl: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/account/billing?canceled=true`,
        }
      }
    );

    if (error || !data?.success) {
      throw new Error(data?.error || 'Failed to create checkout session');
    }

    return data as CheckoutSessionResponse;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setIsProcessing(true);

    try {
      const { sessionUrl } = await createCheckoutSession();
      
      if (sessionUrl) {
        // Try to open in new window first (better for payment security)
        const newWindow = window.open(sessionUrl, '_blank');
        
        // If popup was blocked, fall back to current window
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          toast.raw.info(t('billing.popup_blocked_message'));
          // Fallback: redirect in current window after a short delay
          setTimeout(() => {
            window.location.href = sessionUrl;
          }, 2000);
        } else {
          // Close current dialog since payment is opening in new window
          onPaymentSuccess();
        }
      } else {
        throw new Error(t('billing.checkout_url_missing'));
      }

    } catch (error) {
      console.error('Checkout error:', error);
      toast.raw.error(error instanceof Error ? error.message : t('billing.checkout_failed'));
      setIsProcessing(false);
    }
  };

  const isUpgrade = !currentTier || selectedTier.monthly_price > currentTier.monthly_price;
  const isDowngrade = currentTier && selectedTier.monthly_price < currentTier.monthly_price;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Order Summary */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-3">
        <h3 className="font-semibold flex items-center gap-2">
          <DollarSign className="w-4 h-4" />
          {t('billing.order_summary')}
        </h3>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm">{t('billing.plan')}</span>
            <span className="font-medium">{selectedTier.tier_name}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm">{t('billing.monthly_price')}</span>
            <span className="font-medium">{formatPrice(selectedTier.monthly_price)}{t('billing.per_month')}</span>
          </div>
          
          <div className="flex justify-between items-center text-sm text-muted-foreground">
            <span>{t('billing.includes_monthly_credits', { count: selectedTier.monthly_credits })}</span>
            <span>{selectedTier.monthly_credits} {t('billing.credits_label')}</span>
          </div>
        </div>
        
        <div className="border-t pt-3">
          {isUpgrade && (
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-500">
              <TrendingUp className="w-4 h-4" />
              <span>{t('billing.upgrading_plan')}</span>
            </div>
          )}
          
          {isDowngrade && currentTier && (
            <div className="flex items-center gap-2 text-sm text-orange-600 dark:text-orange-500">
              <TrendingDown className="w-4 h-4" />
              <span>{t('billing.plan_change', { 
                oldPlan: currentTier.tier_name, 
                newPlan: selectedTier.tier_name 
              })}</span>
            </div>
          )}
          
          {!isUpgrade && !isDowngrade && (
            <p className="text-sm text-muted-foreground">
              {t('billing.new_subscription_note')}
            </p>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isProcessing}
        className="w-full"
        size="lg"
      >
        {isProcessing ? (
          <>
            <InlineLoader size="sm" text="" />
            {t('billing.redirecting_to_stripe')}
          </>
        ) : (
          <>
            {t('billing.subscribe')} {formatPrice(selectedTier.monthly_price)}
          </>
        )}
      </Button>
    </form>
  );
};

export const CheckoutPaymentDialog: React.FC<CheckoutPaymentDialogProps> = ({
  isOpen,
  onClose,
  selectedTier,
  currentTier,
  companyData,
  onSuccess,
}) => {
  const { t } = useTranslation('pages');
  
  const handlePaymentSuccess = () => {
    onClose();
    onSuccess();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <MobileDialogContent className="md:max-w-md">
        <DialogHeader className="pb-4 md:pb-6">
          <DialogTitle>{t('billing.complete_payment')}</DialogTitle>
        </DialogHeader>
        
        <div className="flex-shrink min-h-0">
          <CheckoutPaymentForm
            selectedTier={selectedTier}
            currentTier={currentTier}
            companyData={companyData}
            onPaymentSuccess={handlePaymentSuccess}
          />
        </div>
      </MobileDialogContent>
    </Dialog>
  );
};
